// src/components/GamepassPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import '../index.css';
import { useAppConfig } from "../ConfigContext";

const inferSeasonNumber = (seasonId) => {
  if (!seasonId) return 1;
  const match = String(seasonId).match(/(\d+)/);
  const n = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(n) ? n : 1;
};


function GamepassPage({ user, token, setUser, onOpenShop, onNavigate }) {

  const [invByItemId, setInvByItemId] = useState({});
const [invRefreshKey, setInvRefreshKey] = useState(0);

useEffect(() => {
  let cancelled = false;

  const loadInventory = async () => {
    try {
      if (!token) {
        if (!cancelled) setInvByItemId({});
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to load inventory");

      // API returns: { items: [ { item_id, quantity, claimed, ... } ] }
      const rows = Array.isArray(data.items) ? data.items : [];
      const map = {};

      for (const row of rows) {
        const itemId = row?.item_id;
        if (!itemId) continue;

        const key = String(itemId);
        const claimed = !!row?.claimed;

        // If any row is unclaimed => show CLAIM.
        // Only show CLAIMED when *all* owned rows are claimed.
        if (!map[key]) {
          map[key] = { owned: true, hasUnclaimed: !claimed };
        } else if (!claimed) {
          map[key].hasUnclaimed = true;
        }
      }

      if (!cancelled) setInvByItemId(map);
    } catch {
      if (!cancelled) setInvByItemId({});
    }
  };

  loadInventory();
  return () => {
    cancelled = true;
  };
}, [token, user?.id, invRefreshKey]);

const getClaimBadge = ({ prize, canClaimNow }) => {
  const id = prize?.id ? String(prize.id) : null;
  if (!id) return null;

  const type = String(prize?.type ?? prize?.kind ?? '').toLowerCase();

  // Auto-credited rewards should never show CLAIM / CLAIMED
  if (type === 'runes' || type === 'sparks') return null;

  // Only show badge for rewards the user is actually allowed to claim now
  if (!canClaimNow) return null;

  const entry = invByItemId[id];
  if (!entry?.owned) return null;

  return entry.hasUnclaimed ? 'CLAIM' : 'CLAIMED';
};


    const isPrizeOwned = (prize) => {
    const id = prize?.id ? String(prize.id) : null;
    return !!(id && ownedByItemId[id]);
  };

  const [levels, setLevels] = useState([]);
  const [prizeAtCompletion, setPrizeAtCompletion] = useState(0);
  const [itemsById, setItemsById] = useState({});
  const [currentLevel, setCurrentLevel] = useState(
    Number(user?.level ?? 1) || 1
  );

  const guardedNavigate = (page, payload) => {
    if (typeof onNavigate === "function") onNavigate(page, payload);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ownedByItemId, setOwnedByItemId] = useState({});

  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [selectedPrizeSide, setSelectedPrizeSide] = useState(null); // "og" | "free"
  const [showNoRunesModal, setShowNoRunesModal] = useState(false);
  const [hasPass, setHasPass] = useState(
    !!(user?.gamepassActive ?? user?.gamepass_active)
  );

  const { shop, season } = useAppConfig() || {};
  const gamePassPrice = Number(
  shop?.gamePassPrice ?? shop?.GAME_PASS_PRICE ?? 0
);

const gamePassPriceStars = Number(
  shop?.gamePassPriceStars ?? shop?.GAME_PASS_PRICE_STARS ?? 0
);

const shouldUseStars = gamePassPriceStars > 0;
const effectiveGamePassPrice = shouldUseStars ? gamePassPriceStars : gamePassPrice;

  const seasonNumber = useMemo(() => {
    if (season) {
      const candidates = [
        season.number,
        season.season_number,
        season.SEASON_NUMBER,
        season.seasonNumber,
      ];
      const n = candidates.find(
        (v) => v !== undefined && v !== null && String(v).trim() !== ''
      );
      if (n !== undefined && n !== null && String(n).trim() !== '') {
        return n;
      }
    }

    const fallbackId =
      user?.gamepassSeasonId ?? user?.gamepass_season_id;
    return inferSeasonNumber(fallbackId);
  }, [season, user?.gamepassSeasonId, user?.gamepass_season_id]);

  const seasonEndsAt = useMemo(() => {
  const end =
    season?.endsAt ||
    season?.ends_at ||
    season?.endAt ||
    season?.end_at;

  return end ? new Date(end) : new Date("2026-02-28T23:59:59Z");
}, [season]);

const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0, done: false });

useEffect(() => {
  if (!hasPass) return; // only run after purchase/unlocked

  const tick = () => {
    const now = Date.now();
    const end = seasonEndsAt?.getTime?.() ? seasonEndsAt.getTime() : 0;
    const diff = Math.max(0, end - now);

    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);

    setCountdown({ d, h, m, s, done: diff === 0 });
  };

  tick();
  const t = setInterval(tick, 1000);
  return () => clearInterval(t);
}, [hasPass, seasonEndsAt]);


  useEffect(() => {
    const imgs = [
      "/shine.png",
      "/moonsun.png",
      "/ogpass.png",
      "/v2/lock.png"
    ];

    imgs.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/gamepass`,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : undefined,
          }
        );

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to load gamepass');
        }

        if (cancelled) return;

        const rawSeasonId =
          data.seasonId ||
          data.season_id ||
          data.gamepassSeasonId ||
          data.gamepass_season_id;

                setLevels(Array.isArray(data.levels) ? data.levels : []);
        setPrizeAtCompletion(Number(data.prizeAtCompletion || 0));
        setItemsById(data.itemsById || {});
        setHasPass(!!data.hasGamepass);

        const lvl =
          Number(data.currentLevel || user?.level || 1) || 1;
        setCurrentLevel(lvl);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load gamepass');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();






    return () => {
      cancelled = true;
    };
  }, [token, user?.id, user?.level]);

  const totalLevels = levels.length || 30;

    // total levels already defined:
  // const totalLevels = levels.length || 30;

  // 🔥 Is the LAST level (e.g. 30) completed?
  const finalLevelCompleted = useMemo(() => {
    if (!levels.length) return false;

    const total = levels.length || 30;
    const playerLevel = Number(user?.level ?? currentLevel ?? 1);
    const playerLevelTaps = Number(user?.levelTaps ?? 0);

    // We care about the LAST level’s requiredTaps
    const lastLevelConfig =
      levels.find(l => Number(l.level ?? 0) === total) ||
      levels[total - 1];

    const requiredTaps = Number(lastLevelConfig?.requiredTaps ?? 0);
    if (!requiredTaps) return false;

    // “Level 30 complete” means:
    // - user is on or past that last level
    // - AND they have tapped at least requiredTaps for it
    return playerLevel >= total && playerLevelTaps >= requiredTaps;
  }, [levels, user?.level, user?.levelTaps, currentLevel]);


  const progressRatio = useMemo(() => {
  if (!totalLevels) return 0;
  const clamped = Math.min(Math.max(currentLevel, 1), totalLevels);
  return clamped / totalLevels;
}, [currentLevel, totalLevels]);


  const getPrize = (itemId) => {
    if (!itemId) return null;
    return itemsById[itemId] || null;
  };

 const getPrizeIcon = (prize) => {
  const type = String(
    prize?.type ??
    prize?.kind ??
    ""
  )
    .trim()
    .toLowerCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");

  switch (type) {
    case "runes":
      return "/runes.png";

    case "sparks":
      return "/sparks.png";

    case "tapi":
      return "/icon_tapi.png";

    case "booster":
    case "boosters":
    case "energy":
      return "/energy.png";

    case "seasonal_accessory":
    case "seasonalaccessory":
      return "/fragment.png";

    case "ability":
    case "abilities":
      return "/fightlog.png";

    case "shield":
    case "trophy_shield":
      return "/shield.png";

    case "card_pieces":
    case "card_piece":
    case "cardpieces":
      return "/pieces.png";

    case "spins":
    case "spin":
      return "/fortuneicon.png";

    case "evolve":
    case "evolution":
      return "/evolveicon.png";

    case "gamepass":
      return "/ogpass.png";

    case "skin":
    case "skins":
      return "/fragment.png";

    case "chest":
      return (
        prize?.icon ||
        prize?.image ||
        prize?.item_icon ||
        "/pack.png"
      );

    case "card": {
      const rarity = String(
        prize?.rarity ??
        prize?.card_rarity ??
        prize?.cardRarity ??
        prize?.reward_rarity ??
        prize?.rewardRarity ??
        prize?.card?.rarity ??
        ""
      )
        .trim()
        .toLowerCase();

      if (rarity === "legendary") return "/legendary.png";
      if (rarity === "epic") return "/epic.png";
      if (rarity === "rare") return "/rare.png";

      return "/common.png";
    }

    case "name": {
      const category = String(
        prize?.name_category ??
        prize?.nameCategory ??
        ""
      )
        .trim()
        .toLowerCase();

      if (category === "banners" || category === "banner") {
        return prize?.name_value || prize?.image || "/banner_icon.png";
      }

      if (
        category === "emogy" ||
        category === "emoji" ||
        category === "emojy"
      ) {
        return prize?.name_value || prize?.image || "/emogy_icon.png";
      }

      return "/style_icon.png";
    }

    default:
      return (
        prize?.icon ||
        prize?.image ||
        prize?.item_icon ||
        prize?.itemIcon ||
        "/pack.png"
      );
  }
};

const handlePrizeClick = (prize, side, canClaimNow) => {
  if (!prize) return;

  const badge = getClaimBadge({ prize, canClaimNow });
  if (badge !== 'CLAIM') return;

  const prizeType = (prize.type ?? prize.kind ?? '').toLowerCase();

  // runes/sparks never navigate from gamepass claim badge
  if (prizeType === 'runes' || prizeType === 'sparks') return;

  try {
    localStorage.setItem('inventoryView', 'items');
    if (prize?.id) {
      localStorage.setItem('inventoryFocusItemId', String(prize.id));
    }
  } catch {}

  guardedNavigate('inventoryHub', {
  tab: 'items',
  focusItemId: prize?.id,
  from: 'gamepass',
});
};


const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pollStarsOrder = async (orderId, maxAttempts = 40, delayMs = 1500) => {
  for (let i = 0; i < maxAttempts; i++) {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }
    );

    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.status === 'paid') return true;
    if (res.ok && data?.status === 'failed') return false;

    await wait(delayMs);
  }

  return false;
};


  const handleConfirmClick = async () => {
  if (hasPass) {
    setShowUnlockModal(true);
    return;
  }

  if (!effectiveGamePassPrice || effectiveGamePassPrice <= 0) {
    setError('Gamepass is not for sale right now.');
    return;
  }

  // ---------- STARS FLOW ----------
  if (shouldUseStars) {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gamepass/purchase-stars`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 403 && data?.code === 'NEEDS_HUMAN_CHECK') {
          onNavigate?.('humancheck', { returnTo: 'gamepass' });
          return;
        }

        if (data.error === 'Gamepass already active for this season') {
          setHasPass(true);
          setInvRefreshKey((k) => k + 1);
          setShowUnlockModal(true);
          return;
        }

        setError(data.error || 'Failed to create stars payment');
        return;
      }

      const invoiceLink =
        data?.invoiceLink ??
        data?.payment?.invoiceLink ??
        null;

      const orderId =
        data?.orderId ??
        data?.payment?.orderId ??
        null;

      if (!invoiceLink || !orderId) {
        setError('Invalid stars payment response');
        return;
      }

      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) {
        setError('Telegram Stars payments are only available inside Telegram.');
        return;
      }

      tg.openInvoice(invoiceLink, async (status) => {
        if (status !== 'paid') return;

        const paid = await pollStarsOrder(orderId);
        if (!paid) {
          setError('Payment received but confirmation is still pending.');
          return;
        }

        setHasPass(true);
        setInvRefreshKey((k) => k + 1);
        setShowUnlockModal(true);

        if (typeof setUser === 'function') {
          setUser((prev) => ({
            ...prev,
            gamepass_active: true,
          }));
        }
      });

      return;
    } catch (err) {
      console.error('Gamepass stars purchase failed:', err);
      setError('Network error');
      return;
    }
  }

  // ---------- RUNES FLOW ----------
  const currentRunes = Number(user?.runes ?? 0);
  if (currentRunes < gamePassPrice) {
    setShowNoRunesModal(true);
    return;
  }

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/gamepass/purchase`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    const data = await res.json();
    if (!res.ok) {
      if (res.status === 403 && data?.code === 'NEEDS_HUMAN_CHECK') {
        onNavigate?.('humancheck', {
          returnTo: 'gamepass',
        });
        return;
      }

      if (data.error === 'Not enough runes') {
        setShowNoRunesModal(true);
        return;
      }

      if (data.error === 'Gamepass already active for this season') {
        setHasPass(true);
        setInvRefreshKey((k) => k + 1);
        setShowUnlockModal(true);
        return;
      }

      setError(data.error || 'Failed to unlock gamepass');
      return;
    }

    setHasPass(true);
    setInvRefreshKey((k) => k + 1);
    setShowUnlockModal(true);

    if (typeof setUser === 'function' && data.user) {
      setUser((prev) => ({
        ...prev,
        runes: data.user.runes ?? prev?.runes,
        gamepass_active: data.user.gamepass_active ?? prev?.gamepass_active,
        gamepassSeasonId: data.user.gamepass_season_id ?? prev?.gamepassSeasonId,
        level: prev?.level,
        levelTaps: prev?.levelTaps,
      }));
    }
  } catch (err) {
    console.error('Gamepass purchase failed:', err);
    setError('Network error');
  }
};

const renderPrize = (prize, side, isLocked, requiredTaps, canClaimNow) => {
  if (!prize) {
  return (
    <div
      className="gp-prize-empty"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        lineHeight: 1,
      }}
    >
      No Prize
    </div>
  );
}

  const amount = prize.amount ?? prize.value ?? "";
  const name = prize.name ?? prize.label ?? "";
  const type = prize.type ?? prize.kind ?? "";
  const icon = getPrizeIcon(prize);

  const badgeText = getClaimBadge({ prize, canClaimNow });

  return (
    <div
  className="gp-prize"
  onClick={() => handlePrizeClick(prize, side, canClaimNow)}
  style={{
    cursor: badgeText ? "pointer" : "default",
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
  }}
>
      {/* ✅ TOP-RIGHT of the ITEM BLOCK (not the icon) */}
      {badgeText && (
        <div
          className="gp-claim-badge"
          style={{
            position: "absolute",
            top: -12,
            right: -8,
            padding: "2px 8px",
            borderRadius: 5,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 1,
            textTransform: "uppercase",
            background:
              badgeText === "CLAIMED"
                ? "linear-gradient(180deg, rgba(80,80,80,0.9), rgba(40,40,40,0.9))"
                : "linear-gradient(180deg, rgba(255,180,0,0.95), rgba(224,86,3,0.95))",
            color: badgeText === "CLAIMED" ? "#fff" : "#fff",
            border: "1px solid rgba(0,0,0,0.45)",
            textShadow: "1px 1px #000",
            boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
            pointerEvents: "none",
            zIndex: 6,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          {badgeText}
        </div>
      )}

      <div className="gp-prize-icon-wrap">
        <img
          src={icon}
          alt={type}
          className="gp-prize-icon"
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            objectFit: "contain",
          }}
        />

        {/* 🔒 TOP-RIGHT LOCK */}
        {isLocked && (
          <img
            src="/v2/lock.png"
            alt="locked"
            className="gp-prize-lock"
          />
        )}
      </div>

      <div
  className="gp-prize-text"
  style={{
    textAlign: "left",
    alignItems: "flex-start",
  }}
>
  <div className="gp-prize-main">
    {amount ? `${amount} ${name}` : name}
  </div>

  <div
  className="gp-prize-req"
  style={{
    marginTop: 2,
    fontSize: 12,
    opacity: 0.85,
    textAlign: "left",
    textShadow: "1px 1px #000",
    display: "none"
  }}
>
  Requires <b>{Number(requiredTaps || 0)}</b> taps
</div>
</div>

    </div>
  );
};


  // --- render -----------------------------------------------------------------
  return (
    <>
      <div
        className="gamepass-container"
        
      >
        {/* HEADER */}
        <div
          className="gamepass-header"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 70%), url('/moonsun.png'), url('/gamepassbg.png')",
            backgroundSize: '100% 100%, 70px, 100%',
            backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
            backgroundPosition: 'center top, left 10px center, center top',
          }}
        >
          <div className="gp-season-title" style={{position: "relative"}}>
            <div className="gp-season-label">
  TAPTOPIA Season {seasonNumber}
</div>

            <div className="gp-season-name shadow2" style={{marginTop: 4, fontSize: 21}}>Sands of Shadow</div>

          </div>


          <div className="gp-season-timer">
            <span>Season ends in</span>
            <strong>21D 12H 42MIN</strong>
          </div>
        </div>

        {error && <div className="gp-error">{error}</div>}

        {/* PROGRESS BAR */}
        <div className="gp-progress-wrapper">
          <div className="gp-progress-labels shadow1">
            <span>Level Up to Earn Rewards</span>
            <span>
              Level {Math.min(currentLevel, totalLevels)}/{totalLevels}
            </span>
          </div>
          <div className="gp-progress-bar">
            <div
              className="gp-progress-fill"
              style={{
                width: `${
                  Math.max(0, Math.min(1, progressRatio)) * 100
                }%`,
              }}
            />
          </div>
        </div>

        {/* COLUMN HEADERS */}
<div className="gp-columns-header" style={{marginBottom: 0, marginTop: 0}}>
  <div className="gp-col-title gp-col-title-free">
    <div className="game-button-small yellow-button" style={{marginTop:0}}>FREE</div>
  </div>
  <div className="gp-col-title gp-col-title-level" />
  <div className="gp-col-title gp-col-title-og">
    <img src="/pass1.png" style={{ height: '38px' }} />
  </div>
</div>


        {/* LEVELS LIST */}
        <div className="gp-levels-list">
          <div className="gp-levels-inner">
            {loading && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                Loading gamepass…
              </div>
            )}

                                    {!loading &&
              levels.map((lvl, idx) => {
                const levelNumber = lvl.level ?? idx + 1;
                const requiredTaps = Number(lvl.requiredTaps ?? 0);


                const freePrize =
                  getPrize(lvl.prize_item_id || lvl.prizeItemId);
                const passPrize =
                  getPrize(
                    lvl.prize_gamepass_item_id ||
                    lvl.prizeGamepassItemId
                  );

                const numericCurrent = Number(currentLevel || 1);
                const isLastLevel = levelNumber === totalLevels;

                // ---------- COMPLETED / CURRENT ----------
                let isCompleted = false;
                let isCurrent = false;

                if (isLastLevel && finalLevelCompleted) {
                  // ✅ LAST LEVEL *AND* COMPLETED:
                  // treat it like all previous completed levels
                  isCompleted = true;
                  isCurrent = false;
                } else {
                  // normal logic for all other rows
                  isCompleted = levelNumber < numericCurrent;
                  isCurrent = levelNumber === numericCurrent;
                }

                const freeType = String(freePrize?.type ?? freePrize?.kind ?? '').toLowerCase();
const passType = String(passPrize?.type ?? passPrize?.kind ?? '').toLowerCase();

const isFreeAutoClaim = freeType === 'runes' || freeType === 'sparks';
const isPassAutoClaim = passType === 'runes' || passType === 'sparks';

// CLAIM só pode aparecer em reward já liberada daquela trilha
const canClaimFreeNow =
  !hasPass &&
  isCompleted &&
  !isFreeAutoClaim;

const canClaimOgNow =
  hasPass &&
  isCompleted &&
  !isPassAutoClaim;

                // ---------- FREE TRACK ----------
// ---------- FREE TRACK ----------
let isFreeLocked;
let freeOpacity;

if (hasPass) {
  // OG PASS ACTIVE:
  // 👉 FREE PASS SHOULD NEVER SHOW THE LOCK ICON, ONLY BE DIMMED
  isFreeLocked = false;   // no locker icon on any free level
  freeOpacity = 0.45;     // still dim the whole column
} else {
  // OG PASS NOT ACTIVE:
  // 👉 FREE PASS BEHAVES LIKE MAIN TRACK (SAME AS OG LOGIC WHEN MAIN)
  // completed: no icon
  // current:   lock icon
  // future:    lock icon
  isFreeLocked = !isCompleted;
  freeOpacity = 1;
}



                // ---------- OG TRACK ----------
let isOgLocked;
if (!hasPass) {
  // Free player: OG always locked
  isOgLocked = true;
} else {
  // OG PASS ACTIVE:
  // completed => unlocked
  // current+future => locked
  isOgLocked = !isCompleted;
}


                return (
                  <motion.div
                    key={levelNumber}
                    className={`gp-level-row ${
                      isCompleted && !isCurrent ? 'gp-level-completed' : ''
                    }`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.15,
                      delay: idx * 0.015,
                    }}
                  >

                  {/* FREE PASS reward */}
                    <div
                      className="gp-cell gp-cell-free"
                      style={{
                        opacity: freeOpacity,
                        ...(isCurrent && !hasPass
                          ? { border: '1px solid #ffd700', borderRadius: 8 }
                          : {}),
                      }}
                    >
                      {renderPrize(
  freePrize,
  'free',
  isFreeLocked,
  requiredTaps,
  canClaimFreeNow
)}
                    </div>
                    

                    {/* Level number */}
                    <div
                      className="gp-level-badge"
                      style={{
                        backgroundImage: "url('level_bg.png')",
                        backgroundSize: 'cover',
                        backgroundRepeat: 'repeat',
                        backgroundPosition: '100%',
                      }}
                    >
                      <span>{levelNumber}</span>
                    </div>

                    {/* OG PASS reward */}
                    <div
                      className="gp-cell gp-cell-og"
                      style={{
                        opacity: 1,
                        ...(isCurrent && hasPass
                          ? { border: '1px solid #ffd700', borderRadius: 8 }
                          : {}),
                      }}
                    >
                      {renderPrize(
  passPrize,
  'og',
  isOgLocked,
  requiredTaps,
  canClaimOgNow
)}
                    </div>
                    
                  </motion.div>
                );
              })
            }


            {prizeAtCompletion > 0 && (
              <div className="gp-completion-note">
                Reach level {totalLevels} to earn an extra{' '}
                <b>{prizeAtCompletion}</b> Runes!
              </div>
            )}
          </div>
        </div>

        <motion.button
          className="game-button green-button"
          style={{
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginTop: 15,
            marginBottom: 10,
            marginLeft: "auto",
            marginRight: "auto",
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.92 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          onClick={handleConfirmClick}
          disabled={hasPass || effectiveGamePassPrice <= 0}
        >
          {/* label */}
          <span style={{ marginTop: 4 }}>
  {hasPass
  ? "GAMEPASS ACTIVE"
  : effectiveGamePassPrice <= 0
    ? "NOT FOR SALE YET"
    : "UNLOCK"}
</span>

          {/* rune icon + price (only when not unlocked) */}
{!hasPass && effectiveGamePassPrice > 0 && (
  <>
    <img
      src={shouldUseStars ? "/tstar.svg" : "/runes.png"}
      alt={shouldUseStars ? "stars" : "runes"}
      style={{
        width: 26,
        height: 26,
        objectFit: "contain",
        marginTop: 4,
      }}
    />

    <span
      style={{
        display: "none",
        fontSize: 18,
        textShadow: "1px 1px #000",
        marginTop: 4,
        opacity: 0.85,
        textDecoration: "line-through",
        textDecorationThickness: "2px",
        textDecorationColor: "#ff4d4d",
      }}
    >
      300
    </span>

    <span
      style={{
        fontSize: 22,
        textShadow: "1px 1px #000",
        marginTop: 4,
      }}
    >
      {effectiveGamePassPrice}
    </span>
  </>
)}


        </motion.button>
        <br/>
        {hasPass ? (
  <span style={{ fontFamily: '"Fredoka", sans-serif',fontSize: "16px" }}>
    {countdown.done
      ? "Gamepass Ended"
      : `Game Pass ends in ${countdown.d}D ${String(countdown.h).padStart(2, "0")}H ${String(countdown.m).padStart(2, "0")}M ${String(countdown.s).padStart(2, "0")}S`}
    
  </span>
) : (
  <span className="shadow1" style={{ fontWeight: 600, fontFamily: '"Fredoka", sans-serif',fontSize: "16px" }}>Shop Now, Earn More!</span>
)}

      </div>

      {/* OG PASS UNLOCK MODAL */}
      {showUnlockModal && (
        <div
          className="modal_box"
          onClick={() => setShowUnlockModal(false)}
        >
        <div className="modal_title">Success!<button className="modal_close" onClick={() => setShowUnlockModal(false)}/></div>
          <motion.div
            className="modal_body"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.75, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          >
          <div className="modal_inside">

<img src="/v2/lock.png"
                    style={{
                      height: 80,
                      marginBottom: 10
                    }} />

           
              <div className="shadow2" style={{ fontSize: 28, fontWeight: 600,marginBottom: 10}}>
                  Unlocked!
              </div>

              <p className="info">
                Enjoy extra rewards on every level and exclusive OG prizes.
              </p>
              </div>
              <div className="modal_footer">
                <button className="game-button green-button" onClick={() => setShowUnlockModal(false)}>
                  Awesome!
                </button>
              </div>
          </motion.div>
        </div>
      )}

      {showNoRunesModal && (
        <div className="modal-backdrop" onClick={() => setShowNoRunesModal(false)}>
          <motion.div
            className="modal fancy-success-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <img
              src="/runes.png"
              alt="Not enough runes"
              style={{ height: 80, marginBottom: 10 }}
            />
            <h2 style={{ color: '#ffcc00', marginBottom: 10 }}>Not enough Runes</h2>
            <p style={{ textShadow: '1px 1px #000', color: '#ddd' }}>
              You don’t have enough runes to unlock the OG Pass.
              <br />
              Convert Sparks into Runes in the Shop and try again.
            </p>
            <div className="modal-footer">
              <button
  className="buy-runes-btn"
  onClick={() => onOpenShop?.('coin_2')}
>
  Buy Runes
</button>

              <br/>
              <button onClick={() => setShowNoRunesModal(false)}>Close</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* PRIZE DETAILS MODAL */}
      {showPrizeModal && selectedPrize && (
        <div
          className="modal-backdrop"
          onClick={() => setShowPrizeModal(false)}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: 10 }}>
              <img
  src={getPrizeIcon(selectedPrize)}
                alt={selectedPrize.type}
                style={{
                  width: 72,
                  height: 72,
                  objectFit: 'contain',
                  marginBottom: 8,
                }}
              />
              <h2 style={{ margin: 0 }}>
                {selectedPrize.name || 'Prize'}
              </h2>
              {selectedPrizeSide && (
                <p style={{ margin: '4px 0 0', opacity: 0.8 }}>
                  {selectedPrizeSide === 'og'
                    ? 'OG Pass reward'
                    : 'Free Pass reward'}
                </p>
              )}
            </div>

            <p style={{ marginTop: 12 }}>
              Amount:{' '}
              <strong>
                {selectedPrize.amount ?? selectedPrize.value ?? 1}
              </strong>
            </p>

            {(selectedPrize.description ||
              selectedPrize.details) && (
              <p style={{ marginTop: 8, fontSize: 14 }}>
                {selectedPrize.description ||
                  selectedPrize.details}
              </p>
            )}

            <div className="modal-footer">
              <button onClick={() => setShowPrizeModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GamepassPage;
